// src/components/ListingCard/ListingCard.js (updated to handle DynamoDB listings)

import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  IconReviewStar,
  IconSupercap,
  NamedLink,
  ResponsiveImage,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, isDynamoDBListing, isRentalTideListing } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = isDynamoDBListing || isRentalTideListing || displayPrice(foundListingTypeConfig);

  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  let { formattedPrice, priceTitle } = priceData(price, price.currency, intl);

  if (!price?.amount || price.amount === 0) {
    return null;
  }

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        from {formattedPrice}
      </div>
      {isRentalTideListing && formattedPrice !== 'Click for Price' ? (
        <div className={css.perUnit}>
          /{publicData?.priceUnit || 'hr'}
        </div>
      ) : isBookable && formattedPrice !== 'Click for Price' ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

const RatingMaybe = props => {
  const { publicData, isDynamoDBListing } = props;

  if (!isDynamoDBListing) return null;

  const rating = publicData?.rating;
  const reviewCount = publicData?.reviewCount;

  if (!rating || rating === 0) return null;

  // Convert rating to number of filled stars (e.g., 4.97 → 4 filled stars)
  const filledStars = Math.round(rating); // Round to nearest integer for simplicity

  return (
    <div className={css.rating}>
      <div className={css.starsWrapper}>
        {[...Array(5)].map((_, index) => (
          <IconReviewStar
            key={index}
            className={classNames(css.star, {
              [css.starFilled]: index < filledStars,
            })}
          />
        ))}
        <span className={css.ratingText}>
          {rating.toFixed(1)} ({reviewCount || 0} reviews)
        </span>
      </div>
    </div>
  );
};

const ListingLink = props => {
  const { isDynamoDBListing, isRentalTideListing, bookingUrl, children, className, id, slug } = props;

  if (isRentalTideListing) {
    // RentalTide listings — navigate to internal detail page with embedded booking
    return (
      <NamedLink className={className} name="RentalTideListingPage" params={{ id }}>
        {children}
      </NamedLink>
    );
  }

  if (isDynamoDBListing) {
    // Route to our new DynamoDB listing page
    return (
      <NamedLink className={className} name="DynamoDBListingPage" params={{ id }}>
        {children}
      </NamedLink>
    );
  }

  // Regular Sharetribe listing
  return (
    <NamedLink className={className} name="ListingPage" params={{ id, slug }}>
      {children}
    </NamedLink>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
  } = props;

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;

  // Check if this is a DynamoDB or RentalTide listing
  const isDynamoDBListing = publicData?.isDynamoDBListing;
  const isRentalTideListing = publicData?.isRentalTideListing;
  const isExternalListing = isDynamoDBListing || isRentalTideListing;
  const affiliateLink = publicData?.affiliateLink;
  const bookingUrl = publicData?.bookingUrl;

  // Add data attribute for external styling
  const classes = classNames(rootClassName || css.root, className, {
    // Add any conditional classes here if needed
  });

  const mainListingId = publicData?.mainListingId;
  const redirectId = mainListingId ?? id;

  const slug = createSlug(title);
  const author = ensureUser(listing.author);

  let authorName = author.attributes.profile.displayName;

  if (isRentalTideListing) {
    // For RentalTide listings, show operator/location name
    authorName = author.attributes.profile.displayName || '';
  } else if (isDynamoDBListing) {
    // For DynamoDB listings, show the source
    authorName = ' ';
  } else if (authorName === 'WetRentals  ') {
    authorName = '';
  } else {
    authorName = `Hosted by: ${authorName}`;
  }

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const promoted = publicData?.promoteCommission;
  const instantBook = publicData?.instantBook === true;
  const supercap = publicData?.supercap;
  const ourPick = publicData?.ourPick;
  const listingMetadata = currentListing?.attributes?.metadata || {};
  const partner =
    publicData?.isRentalTideListing === true ||
    publicData?.partner === true ||
    publicData?.fareHarbor === true ||
    listingMetadata?.fareHarbor === true;
  const hostMetadata = author?.attributes?.profile?.metadata || {};
  const bookingCount = Number(hostMetadata.hostBookingCount) || 0;
  const hostReviewCount = Number(hostMetadata.hostReviewCount) || 0;
  const hostAverageRating = Number(hostMetadata.hostAverageRating) || 0;
  const createdAtRaw = currentListing.attributes.createdAt;
  const createdAt = createdAtRaw ? new Date(createdAtRaw) : null;
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const isNewListing = createdAt && createdAt > sixMonthsAgo;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <ListingLink
      className={classes}
      isDynamoDBListing={isDynamoDBListing}
      isRentalTideListing={isRentalTideListing}
      bookingUrl={bookingUrl}
      affiliateLink={affiliateLink}
      id={redirectId}
      slug={slug}
    >
      <div className={classes} data-external={isExternalListing ? 'true' : 'false'}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
          {isExternalListing && !partner && (
            <div className={css.externalLabel}>
              <span className={css.externalText}>
                <FormattedMessage id="ListingCard.external" />
              </span>
            </div>
          )}
          {isNewListing && !isExternalListing ? (
            <span
              className={css.newImageBadge}
              data-tooltip="Listed in the last 6 months"
            >
              New
            </span>
          ) : null}
          {!isExternalListing ? (
            <div className={css.pricePill}>
              <PriceMaybe
                price={price}
                publicData={publicData}
                config={config}
                intl={intl}
                isDynamoDBListing={isDynamoDBListing}
                isRentalTideListing={isRentalTideListing}
              />
              {instantBook ? (
                <span
                  className={css.pricePillBolt}
                  data-tooltip="Instant Book — confirmed immediately, no waiting for the host"
                  aria-label="Instant Book"
                >
                  <svg viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 0L0 9h5l-2 7 8-10H6l2-6z" />
                  </svg>
                </span>
              ) : null}
            </div>
          ) : null}
        </AspectRatioWrapper>

        <div className={css.info}>
          {(partner || (!isExternalListing && (supercap || promoted || ourPick))) ? (
            <div className={css.badgeRow}>
              {partner ? (
                <span
                  className={`${css.badge} ${css.badgePartner}`}
                  data-tooltip="Partnered Experience — booked through WetRentals' partner network"
                >
                  <svg className={css.badgeIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M8 1.5L2.5 4v4.6c0 3.1 2.2 5.6 5.5 6.4 3.3-.8 5.5-3.3 5.5-6.4V4L8 1.5z"
                      fill="currentColor"
                    />
                    <path
                      d="M6 8.5l1.5 1.5L10.5 7"
                      stroke="#fff"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Partnered
                </span>
              ) : null}
              {supercap ? (
                <span
                  className={`${css.badge} ${css.badgeSupercap}`}
                  data-tooltip="Super Captain — host with great reviews and a strong track record"
                >
                  <IconSupercap className={css.badgeIcon} />
                  <FormattedMessage id="ListingCard.supercap" />
                </span>
              ) : null}
              {promoted ? (
                <span
                  className={`${css.badge} ${css.badgePromoted}`}
                  data-tooltip="Promoted — host has chosen to boost this listing's visibility"
                >
                  <IconReviewStar className={css.badgeIcon} />
                  <FormattedMessage id="ListingCard.promoted" />
                </span>
              ) : null}
              {ourPick ? (
                <span
                  className={`${css.badge} ${css.badgeOurPick}`}
                  data-tooltip="Our Pick — hand-selected by the WetRentals team"
                >
                  <IconReviewStar className={css.badgeIcon} />
                  <FormattedMessage id="ListingCard.ourPick" />
                </span>
              ) : null}
            </div>
          ) : null}
          {!isExternalListing && bookingCount > 0 ? (
            <div className={css.socialProof}>
              <span className={css.socialProofItem}>
                <strong>{bookingCount}</strong>
                <span className={css.socialProofShort}>
                  {bookingCount === 1 ? ' booking' : ' bookings'}
                </span>
                <span className={css.socialProofLong}>
                  {bookingCount === 1 ? ' host booking' : ' host bookings'}
                </span>
              </span>
            </div>
          ) : null}
          <div className={css.mainInfo}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            <RatingMaybe publicData={publicData} isDynamoDBListing={isDynamoDBListing} />
            {showAuthorInfo ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
                {!isExternalListing && hostReviewCount > 0 ? (
                  <span
                    className={css.hostRating}
                    data-tooltip={`Host rating across ${hostReviewCount} ${hostReviewCount === 1 ? 'review' : 'reviews'}`}
                  >
                    <svg
                      className={css.hostRatingStar}
                      width="11"
                      height="11"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M8 .5l2.4 5 5.1.7-3.7 3.7.9 5.1L8 12.5 3.3 15l.9-5.1L.5 6.2l5.1-.7L8 .5z" />
                    </svg>
                    <strong className={css.hostRatingValue}>{hostAverageRating.toFixed(1)}</strong>
                    <span className={css.hostRatingCount}>({hostReviewCount})</span>
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </ListingLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
